import * as React from 'react';
import * as Images from 'pos/Images';
import { z } from 'zod';

import { Res, StyleDTO } from 'core/api';
import { Result } from 'core/infrastructure';
import { PosScreen, Header, Footer, PhoneOnly, LargeDeviceOnly, BackActionButton, Filler, AcceptActionButton, Content, toShortUserName } from 'pos/ui';
import { Message, Modal } from 'core/ui-next/modal';
import { FormRef, useControlsFor } from 'core/ui-next/controls';
import { VStack, Image, Text, Box } from '@chakra-ui/react';
import { LoginFacade } from 'pos/facades';
import { BasicButton, useEffectOnce } from 'core/ui-next';
import { zNonEmptyString } from 'core/infrastructure-next';
import * as UI from 'core/ui';
import { Colors } from 'core/ui';

type StylableUser = { Id: number, Style: StyleDTO, RequiresPassword: boolean };
type PasswordScreenOptions = { title: string, helpTitle: string }

type Props = { user: StylableUser } & PasswordScreenOptions & Modal.Actions<string>;

const passwordSchema = z.object({
	password: zNonEmptyString(Res.Msg.MSG_REQUIRED_PASSWORD())
});

type PasswordValue = z.infer<typeof passwordSchema>;

function GetPassword(props: Props) {

	const { title, helpTitle, user, cancel, close } = props;
	const { Form, PasswordInput } = useControlsFor<PasswordValue>();

	const formRef = React.useRef<FormRef<PasswordValue>>(null);

	useEffectOnce(() => {
		formRef.current?.setFocus?.('password');
	});

	const onAccept = React.useCallback(async () => {

		const result = await formRef.current!.getValue();
		if (!result.ok)
			return;

		const isValidPassword = await LoginFacade.checkUserPassword(user.Id, result.value.password);
		if (!isValidPassword) {
			await Message.show(Res.Msg.MSG_INVALID_PASSWORD_LBL());
			return;
		}

		close(result.value.password);

	}, [close, user]);

	const onCancel = React.useCallback(async () => cancel(), [cancel]);

	const hasImage = !(user.Style.ImageId === Images.NULL_IMAGE || user.Style.ImageId === '');
	const backgroundColor = Colors.getBackgroundColorAsRgb(user.Style.BackColor);

	return <PosScreen screenId='GET_PASSWORD' size={'small'} useKeyPad={true}>

		<Header title={title} onClose={cancel} />

		<Content>
			<Box paddingX='6' paddingY='4' height={'full'} alignContent={'center'}>
				<VStack w='full' justifyContent={'center'} gap='1' mb='6'>
					<VStack w='full' justifyContent={'center'} gap='2'>
						<VStack gap={'1'} width={'full'}>
							{hasImage ?
								<Image src={Images.imgSrcFromId(user.Style.ImageId)} rounded={'full'} h='12' w='12' />
								: <Box display={'flex'} alignItems={'center'} justifyContent={'center'} fontWeight={'bold'} fontSize={'lg'} h='12' w='12' rounded='full' bg={backgroundColor} color={Colors.getTextColor(backgroundColor)}>
									<Text>{toShortUserName(user.Style.Text)}</Text>
								</Box>
							}
							<Text fontWeight={'semibold'} fontSize={'sm'}>{user.Style.Text}</Text>
						</VStack>
						<Box>
							<Text fontSize={'md'} fontWeight={'bold'} color={{ base: 'neutral.800', _dark: 'white' }}>{'¡' + Res.Web.WELCOME_TO_AGORA_LBL() + '!'}</Text>
						</Box>
					</VStack>
				</VStack>
				<Form initialValue={{ password: '' }} schema={passwordSchema} ref={formRef}>
					<VStack w='full' justifyContent={'center'} gap='6'>
						{helpTitle && <Text fontSize={'md'} fontWeight={'bold'}>{helpTitle}</Text>}
						<PasswordInput linkTo={'password'} placeholder={Res.Msg.MSG_INTRODUCE_YOUR_PASSWORD_LBL()} onKeyDown={UI.whenEnterKey(onAccept)} autoComplete='on' />
					</VStack>
				</Form>
				<LargeDeviceOnly>
					<BasicButton type='success' text={Res.Common.OK_BTN()} onClick={onAccept} marginTop='6' />
				</LargeDeviceOnly>
			</Box>
		</Content>

		<Footer>
			<PhoneOnly>
				<BackActionButton action={onCancel} />
				<Filler />
				<AcceptActionButton action={onAccept} />
			</PhoneOnly>
		</Footer>

	</PosScreen>;
}

export default async function getPassword(user: StylableUser, screenOptions?: PasswordScreenOptions): Promise<Result<string>> {
	return user.RequiresPassword
		? await Modal.show(GetPassword, { user, title: screenOptions?.title ?? Res.Web.GET_PASSWORD(), helpTitle: screenOptions?.helpTitle ?? '' })
		: Result.ok('');
}